import axios from "axios";
import { req, kecapi } from "./req";
import type { KscResponseModel } from "../../../common/core/request";
import type {
  CreateClusterParam,
  CreateClusterResponse,
  DeleteClusterParam,
  DeleteClusterResponse,
  DescribeClusterConfigParam,
  DescribeClusterConfigResponse,
  DescribeClusterListParam,
  DescribeClusterPodCidrParam,
  DescribeClusterPodCidrResponse,
  DescribeClustersForCheckParam,
  DescribeClustersForCheckResponse,
  DescribeClustersParam,
  DescribeClustersResponse,
  DescribeEventLogsParam,
  DescribeEventLogsResponse,
  DescribeNodeGroupNumParam,
  DescribeNodeGroupNumResponse,
  DescribeQuotasExceedParam,
  DescribeQuotasExceedResponse,
  DescribeTagKeysParam,
  DescribeTagKeysResponse,
  ModifyClusterParam,
  ModifyClusterResponse,
  ModifyClusterDeleteProtectParam,
  ModifyClusterDeleteProtectResponse,
  DescribeClusterComponentsParam,
  DescribeClusterComponentsResponse,
  DescribeClusterDetailParam,
  DescribeClusterDetailResponse,
  ModifyClusterKecEventCollectingParam,
  ModifyClusterKecEventCollectingResponse,
} from "./types";
import type { DescribeManagedReadyZonesResponse } from "./types/cluster";
import type { Param } from "@kce2/common/api/types";
import { AxiosRESTDataSource } from "@kce2/common/api/v2/rest-data-source";
import { axiosInstance } from "@kce2/app/axios";
import type { ModifyControlPlaneLogParam } from "./schema/cluster/modify-control-panel-log-component";
import { describeAddonInstances } from "./addon";
import {
  EVENT_ADDON_NAME,
  MIGRATED_EVENT_ADDON_NAME,
  LOG_ADDON_NAME,
  MIGRATED_LOG_ADDON_NAME,
} from "../consts";
import {
  DescribeManagedScaleRecordsResponseSchema,
  DescribeClusterListResponseSchema,
  GetClusterMigrateProgressResponseSchema,
  ModifyAutoScaleConfigResponseSchema,
  CheckMachineTypeResponseSchema,
  type DescribeManagedScaleRecordsParam,
  type GetClusterMigrateProgressParam,
  type ModifyAutoScaleConfigParam,
  type CheckMachineTypeParam,
} from "./schema/cluster";
import {
  DescribeClusterComponentStatusResponseSchema,
  ModifyClusterComponentResponseSchema,
  type DescribeClusterComponentStatusParams,
  type ModifyClusterComponentParams,
} from "./schema/component";
import {
  DescribeNodePoolComponentDefaultParamsResponseSchema,
  type DescribeNodePoolComponentDefaultParamsRequest,
  UpdateNodePoolComponentParamResponseSchema,
  type UpdateNodePoolComponentParamRequest,
  DescribeNodePoolComponentUpdateLogResponseSchema,
  type DescribeNodePoolComponentUpdateLogRequest,
  ModifyComponentUpdateStatusResponseSchema,
  type ModifyComponentUpdateStatusRequest,
} from "./schema/node-pool";
import { DescribeClusterSummaryResponseSchema } from "@kce2/api/schema/cluster";

export async function describeClusterConfig(params: DescribeClusterConfigParam) {
  return axios<DescribeClusterConfigResponse>(req.get("DescribeClusterConfig", params.Region));
}

export async function describeClusters(params: DescribeClustersParam) {
  // console.trace("{api/index.ts} invoke describeClusters");
  return axios<DescribeClustersResponse>(
    req.post("DescribeClusters", params.Region, {
      body: {
        ...params,
        PageSize: params.PageSize ?? 10,
        Page: params.Page ?? 1,
      },
    }),
  );
}

export async function describeClustersForCheck(params: DescribeClustersForCheckParam) {
  return axios<KscResponseModel<DescribeClustersForCheckResponse>>(
    req.post("DescribeClustersForCheck", params.Region, {
      body: {
        ...params,
        PageSize: params.PageSize ?? 10,
        Page: params.Page ?? 1,
      },
    }),
  );
}

export async function describeClusterPodCidr(params: DescribeClusterPodCidrParam) {
  return axios<KscResponseModel<DescribeClusterPodCidrResponse>>(
    req.post("CheckClusterPodCidr", params.Region, {
      body: {
        ...params,
      },
    }),
  );
}

export async function createCluster(params: CreateClusterParam) {
  return axios<KscResponseModel<CreateClusterResponse>>(
    req.post("CreateCluster", params.Region, {
      body: params,
    }),
  );
}

export async function deleteCluster(params: Param<DeleteClusterParam>) {
  return axios<DeleteClusterResponse>(
    req.get("DeleteCluster", params.Region, {
      params,
    }),
  ).then(v => v.data);
}

export async function modifyCluster(params: ModifyClusterParam) {
  return axios<ModifyClusterResponse>(
    req.post("ModifyCluster", params.Region, {
      // params: {},
      body: params,
    }),
  );
}

export async function modifyClusterKceEventCollecting(
  params: ModifyClusterKecEventCollectingParam,
) {
  return axios<KscResponseModel<ModifyClusterKecEventCollectingResponse>>(
    req.get("ModifyClusterKceEventCollecting", params.Region, {
      params,
    }),
  );
}

export async function describeClusterComponents(params: DescribeClusterComponentsParam) {
  return axios<DescribeClusterComponentsResponse>(
    req.get("DescribeClusterComponents", params.Region, {
      params: {},
    }),
  );
}

export async function describeClusterDetail(params: DescribeClusterDetailParam) {
  return axios<KscResponseModel<DescribeClusterDetailResponse>>(
    req.get("DescribeClusterDetail", params.Region, {
      params,
    }),
  );
}

export async function describeEventLogs(params: DescribeEventLogsParam) {
  return axios<KscResponseModel<DescribeEventLogsResponse>>(
    req.post("DescribeEventLogs", params.Region, {
      body: params,
    }),
  );
}

export async function describeNodeGroupNum(params: DescribeNodeGroupNumParam) {
  return axios<KscResponseModel<DescribeNodeGroupNumResponse>>(
    req.get("DescribeNodeGroupNum", params.Region, {
      params,
    }),
  );
}

export async function describeTagKeys(params: DescribeTagKeysParam) {
  return axios<DescribeTagKeysResponse>(
    kecapi.get("DescribeTagKeys", params.Region, {
      params: {
        Service: "tag",
        resourceType: "kec-instance",
        maxResults: 1000,
        source: "kec",
      },
    }),
  );
}

export async function describeQuotasExceed(params: DescribeQuotasExceedParam) {
  return axios<KscResponseModel<DescribeQuotasExceedResponse>>(
    req.get("DescribeQuotasExceed", params.Region, {
      params,
    }),
  );
}

export async function modifyClusterDeleteProtect(params: ModifyClusterDeleteProtectParam) {
  return axios<KscResponseModel<ModifyClusterDeleteProtectResponse>>(
    req.post("ModifyClusterDeleteProtect", params.Region, {
      params,
    }),
  );
}

export async function describeManagedReadyZones(params: Param) {
  return axios<KscResponseModel<DescribeManagedReadyZonesResponse>>(
    req.get("DescribeManagedReadyZones", params.Region),
  );
}

export class ClusterAPI extends AxiosRESTDataSource {
  baseURL = "/api/cluster";

  async checkLogAddonInstance(params: Param<{ K8sVersion: string; ClusterId: string }>) {
    const addonNames = [LOG_ADDON_NAME, MIGRATED_LOG_ADDON_NAME];
    const res = await describeAddonInstances({
      Region: params.Region,
      ClusterId: params.ClusterId,
      AddonNames: addonNames,
    });

    return (
      res.data.Data.Addons.find(
        item =>
          item.AddonId.startsWith(LOG_ADDON_NAME) ||
          item.AddonId.startsWith(MIGRATED_LOG_ADDON_NAME),
      ) ?? null
    );
  }

  async checkEventAddonInstance(params: Param<{ ClusterId: string }>) {
    const names = [EVENT_ADDON_NAME, MIGRATED_EVENT_ADDON_NAME];
    const res = await describeAddonInstances({
      Region: params.Region,
      ClusterId: params.ClusterId,
      AddonNames: names,
    });

    return (
      res.data.Data.Addons.find(
        item =>
          item.AddonId.startsWith(EVENT_ADDON_NAME) ||
          item.AddonId.startsWith(MIGRATED_EVENT_ADDON_NAME),
      ) ?? null
    );
  }

  /**
   * • 当Enable为true时
   *    表示需要收集的日志组件：
   *    ◦ APISERVER
   *    ◦ CONTROLLERMANAGER
   *    ◦ SCHEDULER
   *    ◦ AUDIT
   *    修改配置时允许列表为空，开启日志采集时不允许
   * • 当Enable为false时，列表表示需要删除的日志池
   */
  async modifyControlPlaneLog({ Region, ...params }: Param<ModifyControlPlaneLogParam>) {
    return this.post("", undefined, {
      Action: "ModifyControlPlaneLog",
      Region,
      body: params,
    });
  }

  async modifyAutoScaleConfig({ Region, ...params }: Param<ModifyAutoScaleConfigParam>) {
    return this.post("", ModifyAutoScaleConfigResponseSchema, {
      Action: "ModifyAutoScaleConfig",
      Region,
      body: params,
    });
  }

  async describeManagedScaleRecords({
    Region,
    ...params
  }: Param<DescribeManagedScaleRecordsParam>) {
    return this.post("", DescribeManagedScaleRecordsResponseSchema, {
      Action: "DescribeManagedScaleRecords",
      Region,
      body: params,
    });
  }

  async checkMachineType({ Region, ...params }: Param<CheckMachineTypeParam>) {
    return this.get("", CheckMachineTypeResponseSchema, {
      Action: "CheckMachineType",
      Region,
      params,
    });
  }

  async describeClusterComponentStatus({
    Region,
    ...data
  }: Param<DescribeClusterComponentStatusParams>) {
    return this.post("", DescribeClusterComponentStatusResponseSchema, {
      Region,
      Action: "DescribeClusterComponentStatus",
      data,
    });
  }

  async modifyClusterComponents({ Region, Args, ...params }: Param<ModifyClusterComponentParams>) {
    return this.post("", ModifyClusterComponentResponseSchema, {
      Action: "ModifyClusterComponent",
      Region,
      params,
      body: { Args },
    });
  }

  async describeClusterSummary({ Region, ...params }: Param) {
    return this.get("", DescribeClusterSummaryResponseSchema, {
      Action: "DescribeClusterSummary",
      Region,
      params,
    });
  }

  async getClusterMigrateProgress({ Region, ...params }: Param<GetClusterMigrateProgressParam>) {
    return this.post("", GetClusterMigrateProgressResponseSchema, {
      Action: "GetClusterMigrateProgress",
      Region,
      body: params,
    });
  }

  async describeClusterList({ Region, ...params }: Param<DescribeClusterListParam>) {
    return this.post("", DescribeClusterListResponseSchema, {
      Action: "DescribeClusterList",
      Region,
      body: params,
    });
  }

  async describeNodePoolComponentDefaultParams({
    Region,
    ...params
  }: Param<DescribeNodePoolComponentDefaultParamsRequest>) {
    return this.get("", DescribeNodePoolComponentDefaultParamsResponseSchema, {
      Action: "DescribeNodePoolComponentDefaultParams",
      Region,
      params,
    });
  }

  async updateNodePoolComponentParam({
    Region,
    ...params
  }: Param<UpdateNodePoolComponentParamRequest>) {
    return this.post("", UpdateNodePoolComponentParamResponseSchema, {
      Action: "UpdateNodePoolComponentParam",
      Region,
      body: params,
    });
  }

  async describeNodePoolComponentUpdateLog({
    Region,
    ...params
  }: Param<DescribeNodePoolComponentUpdateLogRequest>) {
    return this.post("", DescribeNodePoolComponentUpdateLogResponseSchema, {
      Action: "DescribeNodePoolComponentUpdateLog",
      Region,
      body: params,
    });
  }

  async modifyComponentUpdateStatus({
    Region,
    ...params
  }: Param<ModifyComponentUpdateStatusRequest>) {
    return this.post("", ModifyComponentUpdateStatusResponseSchema, {
      Action: "ModifyComponentUpdateStatus",
      Region,
      body: params,
    });
  }
}

export const clusterAPI = new ClusterAPI(axiosInstance);
export const clusterApi = clusterAPI;
